import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Incarico } from '../types';
import { estraiNumeroMandato, formatOreInHM } from '../utils/utils';

// Importi in formato italiano (35,00), come nel resto del gestionale.
const euroIt = (v: number): string =>
    (Number(v) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });


// Ore per i totali del PDF: "Xh Ym", oppure "0h". Usa formatOreInHM come
// SpeseCollaboratoreView per avere lo stesso formato.
const fmtHTot = (h: number | null | undefined): string => {
    const v = Number(h) || 0;
    if (v <= 0) return '0h';
    return formatOreInHM(v);
};
// Per le celle dettaglio: "-" quando 0, altrimenti "Xh Ym".
const fmtHCell = (h: number | null | undefined): string => {
    const v = Number(h) || 0;
    if (v <= 0) return '-';
    return formatOreInHM(v);
};

const MESI_IT = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

function getPraticaNome(inc: Incarico): string {
    const raw = (inc.pratica as any)?.name || '';
    return raw.includes(' - ') ? raw.split(' - ')[0] : raw || `#${inc.id}`;
}

function getSoggettoNome(inc: Incarico): string {
    if (inc.soggettoNome) return inc.soggettoNome;
    try {
        const raw = (inc.pratica as any)?.datiAggiuntiviJson;
        const dati = typeof raw === 'string' ? JSON.parse(raw) : raw || {};
        return dati.soggetti?.[0]?.nome || dati.soggetto_nome || dati.soggetto_denominazione || '-';
    } catch { return '-'; }
}

function getOreFromSessions(inc: Incarico, year: number, month: number): number {
    if (!inc.sessioni?.length) return 0;
    return inc.sessioni.reduce((acc, s) => {
        if (!s.startTime || !s.endTime) return acc;
        const start = new Date(s.startTime);
        if (start.getFullYear() !== year || start.getMonth() + 1 !== month) return acc;
        const ms = new Date(s.endTime).getTime() - start.getTime();
        return acc + Math.max(0, ms / 3_600_000);
    }, 0);
}

function getRelazioni(inc: Incarico, year: number, month: number): any[] {
    if (!inc.relazioni?.length) return [];
    return inc.relazioni.filter((r: any) => {
        if (!r.dataRelazione) return false;
        const d = new Date(r.dataRelazione);
        return d.getFullYear() === year && (d.getMonth() + 1) === month;
    });
}

interface RigaRiepilogo {
    data: string;
    pratica: string;
    soggetto: string;
    luogo: string;
    oreGps: number;
    oreExtra: number;
    km: number;
}

export function generaRiepilogoCollaboratore(
    allIncarichi: Incarico[],
    monthStr: string,   // YYYY-MM
    nomeCollaboratore?: string,
): void {
    const [year, month] = monthStr.split('-').map(Number);
    const meseLabel = `${MESI_IT[month - 1]} ${year}`;

    // Filtra incarichi: dataInizio nel mese OPPURE ha relazioni nel mese
    const incarichiDelMese = allIncarichi.filter(inc => {
        if (inc.dataInizio) {
            const d = new Date(inc.dataInizio);
            if (d.getFullYear() === year && (d.getMonth() + 1) === month) return true;
        }
        return getRelazioni(inc, year, month).length > 0;
    });

    const righe: RigaRiepilogo[] = incarichiDelMese.map(inc => {
        const relazMese = getRelazioni(inc, year, month);
        const oreGps   = relazMese.reduce((s, r) => s + (r.oreGps   || 0), 0);
        const oreExtra = relazMese.reduce((s, r) => s + (r.oreExtra  || 0), 0);
        const km       = relazMese.reduce((s, r) => s + (r.km || r.kmPercorsi || 0), 0);

        // Fallback ore da sessioni se nessuna relazione
        const oreEffettive = (oreGps + oreExtra) > 0
            ? oreGps + oreExtra
            : getOreFromSessions(inc, year, month);

        const dataStr = inc.dataInizio
            ? new Date(inc.dataInizio).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
            : '-';

        return {
            data: dataStr,
            pratica: getPraticaNome(inc),
            soggetto: getSoggettoNome(inc),
            luogo: inc.luogo || '-',
            oreGps,
            oreExtra,
            km,
            _oreEffettive: oreEffettive,
        } as any;
    });

    const totOre = righe.reduce((s, r) => s + ((r as any)._oreEffettive || 0), 0);
    const totKm  = righe.reduce((s, r) => s + r.km, 0);

    // ─── PDF ───
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();

    // Header
    doc.setFillColor(30, 46, 69); // navy-700
    doc.rect(0, 0, W, 22, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('RIEPILOGO ATTIVITÀ', 14, 10);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(meseLabel.toUpperCase(), 14, 16);

    if (nomeCollaboratore) {
        doc.setFontSize(9);
        doc.text(nomeCollaboratore, W - 14, 10, { align: 'right' });
    }
    doc.text(`Generato il ${new Date().toLocaleDateString('it-IT')}`, W - 14, 16, { align: 'right' });

    // Tabella
    const bodyRows = righe.map(r => [
        r.data,
        r.pratica,
        r.soggetto,
        r.oreGps > 0 ? r.oreGps.toFixed(1) : '-',
        r.oreExtra > 0 ? r.oreExtra.toFixed(1) : '-',
        ((r as any)._oreEffettive || 0) > 0 ? ((r as any)._oreEffettive as number).toFixed(1) : '-',
        r.km > 0 ? `${r.km}` : '-',
    ]);

    // Totali
    bodyRows.push([
        { content: 'TOTALI', colSpan: 5, styles: { fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
        { content: totOre > 0 ? totOre.toFixed(1) : '0', styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
        { content: totKm > 0 ? `${totKm}` : '0', styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
    ] as any);

    autoTable(doc, {
        startY: 28,
        head: [['Data', 'Pratica', 'Soggetto Investigato', 'Ore Registrate', 'Ore Extra', 'Tot. Ore', 'Km']],
        body: bodyRows as any,
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [139, 26, 26], textColor: 255, fontStyle: 'bold', fontSize: 8 },
        columnStyles: {
            0: { cellWidth: 22, halign: 'center' },
            1: { cellWidth: 65 },
            2: { cellWidth: 70 },
            3: { cellWidth: 22, halign: 'center' },
            4: { cellWidth: 22, halign: 'center' },
            5: { cellWidth: 22, halign: 'center' },
            6: { cellWidth: 18, halign: 'center' },
        },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        margin: { left: 14, right: 14 },
    });

    // Footer pagine
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(150);
        doc.text(`Pag. ${i} / ${pageCount}`, W - 14, doc.internal.pageSize.getHeight() - 6, { align: 'right' });
        doc.text('Documento ad uso interno — non riporta compensi né tariffe', 14, doc.internal.pageSize.getHeight() - 6);
    }

    doc.save(`Riepilogo_${meseLabel.replace(' ', '_')}.pdf`);
}

// ─── PDF riepilogo del collaboratore (da /incarichi) ───
// A differenza della versione per il titolare ha colonne proprie e una sezione
// "Servizi fiduciari" con i turni del mese, anche senza relazione.
//
// Incarichi (relazioni con turnoId nullo):
//   Data | Mandato | Ore reg. | Ore extra | Tot ore |
//   Km auto propria | Km auto aziendale | Spese senza carta az.
//
// Servizi (turni del mese), con Servizio+Indirizzo al posto del Mandato:
//   Data | Servizio | Indirizzo | Ore reg. | Ore extra | Ore appr. |
//   Km auto propria | Km auto aziendale | Spese senza carta az.
export function generaRiepilogoCollab(
    relazioni: any[],
    turni: any[],
    monthStr: string,
    nomeCollaboratore: string,
): void {
    const [year, month] = monthStr.split('-').map(Number);
    const meseLabel = `${MESI_IT[month - 1]} ${year}`;

    // Solo relazioni investigative (turnoId nullo): quelle fiduciarie finiscono
    // nella tabella servizi tramite il turno, per non contarle due volte.
    const relazioniInc = relazioni
        .filter(r => r.turnoId == null)
        .sort((a, b) => (a.dataRelazione || '').localeCompare(b.dataRelazione || ''));

    // Turni del mese, ordinati per dataTurno crescente.
    const turniMese = turni
        .filter(t => typeof t.dataTurno === 'string' && t.dataTurno.startsWith(monthStr))
        .sort((a, b) => (a.dataTurno || '').localeCompare(b.dataTurno || ''));

    // Ore/km approvati per relazione, con la stessa logica di Agenda Collaboratore
    // e SpeseCollaboratoreView:
    //   - contano solo le relazioni APPROVATO;
    //   - ore: oreApprovateTotali (override) > oreGps + oreExtraApprovate (o oreExtra se null);
    //   - km: kmApprovati > kmPercorsi.
    // Per le non approvate ritorna null: in tabella "-" e fuori dai totali.
    const oreApprRel = (r: any): number | null => {
        if (!r || r.stato !== 'APPROVATO') return null;
        // Report fiduciario: l'override approvato è sul turno (turnoOreCollaboratoreOverride),
        // come in SpeseCollaboratoreView.
        if (r.turnoId != null && r.turnoOreCollaboratoreOverride != null) {
            return Math.max(0, Number(r.turnoOreCollaboratoreOverride));
        }
        if (r.oreApprovateTotali != null) return Math.max(0, Number(r.oreApprovateTotali));
        const extra = r.oreExtraApprovate != null ? Number(r.oreExtraApprovate) : Number(r.oreExtra || 0);
        return Math.max(0, Number(r.oreGps || 0) + Math.max(0, extra));
    };
    const kmApprRel = (r: any): number => {
        if (!r || r.stato !== 'APPROVATO') return 0;
        return Math.max(0, Number(r.kmApprovati ?? r.kmPercorsi ?? r.km ?? 0));
    };

    // ── Totali tabella incarichi ──
    const totOreReg   = relazioniInc.reduce((s, r) => s + (r.oreGps   || 0), 0);
    const totOreExtra = relazioniInc.reduce((s, r) => s + (r.oreExtra || 0), 0);
    // Solo le ore approvate, non bozze o relazioni da revisionare.
    const totOreAppr  = relazioniInc.reduce((s, r) => s + (oreApprRel(r) ?? 0), 0);
    // autoAziendale è un booleano per relazione: i km della giornata vanno tutti
    // in una colonna. Si sommano solo i km approvati, cioè quelli che verranno
    // effettivamente riconosciuti.
    const totKmPropria   = relazioniInc.reduce((s, r) => s + (r.autoAziendale ? 0 : kmApprRel(r)), 0);
    const totKmAziendale = relazioniInc.reduce((s, r) => s + (r.autoAziendale ? kmApprRel(r) : 0), 0);
    // Spese senza carta aziendale: somma di importoApprovato (o importo) delle spese
    // della relazione non anticipate dall'azienda.
    const sumSpeseCassa = (r: any): number => {
        const spese: any[] = Array.isArray(r.speseEspanso) ? r.speseEspanso : (Array.isArray(r.spese) ? r.spese : []);
        return spese
            .filter(s => !s.anticipatoAzienda)
            .reduce((acc, s) => acc + (Number(s.importoApprovato ?? s.importo) || 0), 0);
    };
    const totSpeseCassa = relazioniInc.reduce((s, r) => s + sumSpeseCassa(r), 0);

    // ── Totali servizi fiduciari ──
    // Il report fine turno produce una relazione con km/spese/autoAziendale come per
    // gli incarichi: stessa formula sulle relazioni con turnoId.
    const relazioniServ = relazioni
        .filter(r => r.turnoId != null && typeof r.dataRelazione === 'string' && r.dataRelazione.startsWith(monthStr));
    const totOreRegServ   = relazioniServ.reduce((s, r) => s + (r.oreGps   || 0), 0);
    const totOreExtraServ = relazioniServ.reduce((s, r) => s + (r.oreExtra || 0), 0);
    const totOreApprServ  = relazioniServ.reduce((s, r) => s + (oreApprRel(r) ?? 0), 0);
    const totKmPropriaServ   = relazioniServ.reduce((s, r) => s + (r.autoAziendale ? 0 : kmApprRel(r)), 0);
    const totKmAziendaleServ = relazioniServ.reduce((s, r) => s + (r.autoAziendale ? kmApprRel(r) : 0), 0);
    const totSpeseCassaServ  = relazioniServ.reduce((s, r) => s + sumSpeseCassa(r), 0);

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();

    // ── Header ──
    doc.setFillColor(30, 46, 69);
    doc.rect(0, 0, W, 26, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('RIEPILOGO ATTIVITÀ', 14, 10);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(nomeCollaboratore, 14, 17);
    doc.setFontSize(8);
    doc.text(meseLabel.toUpperCase(), 14, 23);
    doc.text(`Generato il ${new Date().toLocaleDateString('it-IT')}`, W - 14, 23, { align: 'right' });

    // ── KPI su due righe (Incarichi / Servizi) ──
    // Cinque box ciascuna: ore, km propria, km aziendale, spese, numero.
    // Righe separate perché i km di un incarico e di un servizio hanno un peso economico diverso.
    const renderKpiRow = (kpis: Array<{ label: string; value: string }>, sectionLabel: string, y: number) => {
        // Etichetta sezione a sinistra del primo box.
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.setFont('helvetica', 'bold');
        doc.text(sectionLabel.toUpperCase(), 14, y - 1);
        const kpiW = (W - 28) / kpis.length;
        kpis.forEach((k, i) => {
            const x = 14 + i * kpiW;
            doc.setFillColor(248, 250, 252);
            doc.setDrawColor(226, 232, 240);
            doc.roundedRect(x, y, kpiW - 2, 16, 2, 2, 'FD');
            doc.setFontSize(7);
            doc.setTextColor(100, 116, 139);
            doc.setFont('helvetica', 'normal');
            doc.text(k.label.toUpperCase(), x + (kpiW - 2) / 2, y + 5, { align: 'center' });
            doc.setFontSize(11);
            doc.setTextColor(30, 46, 69);
            doc.setFont('helvetica', 'bold');
            doc.text(k.value, x + (kpiW - 2) / 2, y + 12, { align: 'center' });
        });
    };

    const kpiIncY = 34;
    renderKpiRow([
        { label: 'N° Incarichi',  value: `${relazioniInc.length}` },
        // Ore approvate e non totali inviate: il collaboratore vede quanto gli sarà riconosciuto.
        { label: 'Ore appr.',     value: fmtHTot(totOreAppr) },
        { label: 'Km Propria',    value: `${totKmPropria}` },
        { label: 'Km Aziendale',  value: `${totKmAziendale}` },
        { label: 'Spese Cassa €', value: euroIt(totSpeseCassa) },
    ], 'Incarichi', kpiIncY);

    const kpiServY = kpiIncY + 22;
    renderKpiRow([
        { label: 'N° Servizi',    value: `${turniMese.length}` },
        { label: 'Ore appr.',     value: fmtHTot(totOreApprServ) },
        { label: 'Km Propria',    value: `${totKmPropriaServ}` },
        { label: 'Km Aziendale',  value: `${totKmAziendaleServ}` },
        { label: 'Spese Cassa €', value: euroIt(totSpeseCassaServ) },
    ], 'Servizi Fiduciari', kpiServY);

    // ── Tabella Incarichi ──
    let cursorY = kpiServY + 22;
    doc.setFontSize(10);
    doc.setTextColor(67, 56, 202); // indigo: sezione Investigazioni
    doc.setFont('helvetica', 'bold');
    doc.text('Incarichi Investigativi', 14, cursorY);
    cursorY += 3;

    const incBody = relazioniInc.map(r => {
        const data = r.dataRelazione
            ? new Date(r.dataRelazione).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
            : '-';
        // Mai nome di cliente o soggetto: si mostra il riferimento del mandato o,
        // se manca, solo il numero.
        const riferimento = typeof r.praticaRiferimento === 'string' ? r.praticaRiferimento.trim() : '';
        const mandato = riferimento || estraiNumeroMandato(r.praticaRef || r.praticaNome || r.praticaId);
        const oreReg   = fmtHCell(r.oreGps);
        const oreExtra = fmtHCell(r.oreExtra);
        const oreApp   = oreApprRel(r);
        const oreAppStr = oreApp != null && oreApp > 0 ? formatOreInHM(oreApp) : '-';
        // Km approvati se la relazione è approvata (possono essere stati rettificati),
        // altrimenti quelli richiesti, solo a titolo informativo.
        const isAppr = r.stato === 'APPROVATO';
        const km     = isAppr ? kmApprRel(r) : Number(r.kmPercorsi ?? r.km ?? 0);
        const kmPro  = (!r.autoAziendale && km > 0) ? `${km}` : '-';
        const kmAz   = (r.autoAziendale  && km > 0) ? `${km}` : '-';
        const spCassa  = sumSpeseCassa(r);
        const spCassaStr = spCassa > 0 ? `€ ${euroIt(spCassa)}` : '-';
        return [data, mandato, oreReg, oreExtra, oreAppStr, kmPro, kmAz, spCassaStr];
    });

    if (incBody.length > 0) {
        incBody.push([
            { content: 'TOTALI', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
            { content: fmtHTot(totOreReg),    styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
            { content: fmtHTot(totOreExtra),  styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
            { content: fmtHTot(totOreAppr),   styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
            { content: totKmPropria > 0 ? `${totKmPropria}` : '0',       styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
            { content: totKmAziendale > 0 ? `${totKmAziendale}` : '0',   styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
            { content: totSpeseCassa > 0 ? `€ ${euroIt(totSpeseCassa)}` : '0', styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
        ] as any);

        autoTable(doc, {
            startY: cursorY,
            head: [['Data', 'Mandato', 'Ore reg.', 'Ore extra', 'Ore appr.', 'Km auto propria', 'Km auto aziendale', 'Spese senza carta az.']],
            body: incBody as any,
            theme: 'grid',
            styles: { fontSize: 7.5, cellPadding: 2.5, overflow: 'linebreak' },
            headStyles: { fillColor: [67, 56, 202], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
            columnStyles: {
                0: { cellWidth: 22, halign: 'center' },
                1: { cellWidth: 'auto' },
                2: { cellWidth: 20, halign: 'center' },
                3: { cellWidth: 20, halign: 'center' },
                4: { cellWidth: 20, halign: 'center' },
                5: { cellWidth: 26, halign: 'center' },
                6: { cellWidth: 30, halign: 'center' },
                7: { cellWidth: 32, halign: 'center' },
            },
            alternateRowStyles: { fillColor: [249, 250, 251] },
            margin: { left: 14, right: 14 },
        });
        cursorY = (doc as any).lastAutoTable.finalY + 8;
    } else {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(120, 120, 120);
        doc.text('Nessun incarico investigativo nel mese.', 14, cursorY + 5);
        cursorY += 10;
    }

    // ── Tabella Servizi Fiduciari ──
    doc.setFontSize(10);
    doc.setTextColor(21, 128, 61); // verde: sezione Servizi Fiduciari
    doc.setFont('helvetica', 'bold');
    doc.text('Servizi Fiduciari', 14, cursorY);
    cursorY += 3;

    // Il report fine turno fornisce ore, km e spese del servizio, con le stesse
    // formule della tabella Incarichi.
    const relByTurno = new Map<any, any>();
    relazioniServ.forEach(r => { if (r.turnoId != null) relByTurno.set(r.turnoId, r); });

    const servBody = turniMese.map(t => {
        const data = t.dataTurno
            ? new Date(t.dataTurno).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
            : '-';
        const servNome = t.servizio?.nome || '-';
        const indirizzo = t.servizio?.indirizzo || '-';
        const rel = relByTurno.get(t.id);
        const oreReg   = fmtHCell(rel?.oreGps);
        const oreExtra = fmtHCell(rel?.oreExtra);
        const oreApp   = rel ? oreApprRel(rel) : null;
        const oreAppStr = oreApp != null && oreApp > 0 ? formatOreInHM(oreApp) : '-';
        const isAppr = rel?.stato === 'APPROVATO';
        const km     = rel ? (isAppr ? kmApprRel(rel) : Number(rel.kmPercorsi ?? rel.km ?? 0)) : 0;
        const kmPro  = (rel && !rel.autoAziendale && km > 0) ? `${km}` : '-';
        const kmAz   = (rel &&  rel.autoAziendale && km > 0) ? `${km}` : '-';
        const spCassa  = rel ? sumSpeseCassa(rel) : 0;
        const spCassaStr = spCassa > 0 ? `€ ${euroIt(spCassa)}` : '-';
        return [data, servNome, indirizzo, oreReg, oreExtra, oreAppStr, kmPro, kmAz, spCassaStr];
    });

    if (servBody.length > 0) {
        servBody.push([
            { content: 'TOTALI', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
            { content: fmtHTot(totOreRegServ),   styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
            { content: fmtHTot(totOreExtraServ), styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
            { content: fmtHTot(totOreApprServ),  styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
            { content: totKmPropriaServ > 0 ? `${totKmPropriaServ}` : '0',     styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
            { content: totKmAziendaleServ > 0 ? `${totKmAziendaleServ}` : '0', styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
            { content: totSpeseCassaServ > 0 ? `€ ${euroIt(totSpeseCassaServ)}` : '0', styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
        ] as any);

        autoTable(doc, {
            startY: cursorY,
            head: [['Data', 'Servizio', 'Indirizzo', 'Ore reg.', 'Ore extra', 'Ore appr.', 'Km auto propria', 'Km auto aziendale', 'Spese senza carta az.']],
            body: servBody as any,
            theme: 'grid',
            styles: { fontSize: 7.5, cellPadding: 2.5, overflow: 'linebreak' },
            headStyles: { fillColor: [21, 128, 61], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
            columnStyles: {
                0: { cellWidth: 22, halign: 'center' },
                1: { cellWidth: 'auto' },
                2: { cellWidth: 40 },
                3: { cellWidth: 18, halign: 'center' },
                4: { cellWidth: 18, halign: 'center' },
                5: { cellWidth: 18, halign: 'center' },
                6: { cellWidth: 24, halign: 'center' },
                7: { cellWidth: 26, halign: 'center' },
                8: { cellWidth: 28, halign: 'center' },
            },
            alternateRowStyles: { fillColor: [249, 250, 251] },
            margin: { left: 14, right: 14 },
        });
    } else {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(120, 120, 120);
        doc.text('Nessun servizio fiduciario nel mese.', 14, cursorY + 5);
    }

    // ── Footer pagine ──
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(150);
        doc.text(`Pag. ${i} / ${pageCount}`, W - 14, doc.internal.pageSize.getHeight() - 6, { align: 'right' });
        doc.text('Documento ad uso interno — non riporta compensi né tariffe', 14, doc.internal.pageSize.getHeight() - 6);
    }

    doc.save(`Riepilogo_${nomeCollaboratore.replace(/\s+/g, '_')}_${meseLabel.replace(' ', '_')}.pdf`);
}

// ─── Versione admin: relazioni già filtrate per collaboratore ───
export function generaRiepilogoAdminCollaboratore(
    relazioni: any[],
    monthStr: string,
    nomeCollaboratore: string,
): void {
    const [year, month] = monthStr.split('-').map(Number);
    const meseLabel = `${MESI_IT[month - 1]} ${year}`;

    const calcolaOreCliente = (inizio: string, fine: string): number => {
        if (!inizio || !fine) return 0;
        const [h1, m1] = inizio.split(':').map(Number);
        const [h2, m2] = fine.split(':').map(Number);
        // Fine < inizio: il turno passa la mezzanotte.
        let diff = (h2 * 60 + m2) - (h1 * 60 + m1);
        if (diff < 0) diff += 24 * 60;
        return Math.max(0, Math.round(diff / 6) / 10);
    };

    const sorted = [...relazioni].sort((a, b) =>
        (a.dataRelazione || '').localeCompare(b.dataRelazione || '')
    );

    const totOreGps  = sorted.reduce((s, r) => s + (r.oreGps   || 0), 0);
    const totOreExtra = sorted.reduce((s, r) => s + (r.oreExtra || 0), 0);
    const totKm      = sorted.reduce((s, r) => s + (r.kmPercorsi || r.km || 0), 0);
    const totOreCliente = sorted.reduce((s, r) => s + calcolaOreCliente(r.orarioInizioIndagine, r.orarioFineIndagine), 0);

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();

    // Header navy
    doc.setFillColor(30, 46, 69);
    doc.rect(0, 0, W, 26, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('RIEPILOGO ATTIVITÀ', 14, 10);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(nomeCollaboratore, 14, 17);
    doc.setFontSize(8);
    doc.text(meseLabel.toUpperCase(), 14, 23);
    doc.text(`Generato il ${new Date().toLocaleDateString('it-IT')}`, W - 14, 23, { align: 'right' });

    // KPI bar
    const kpiY = 32;
    const kpis = [
        { label: 'Ore GPS', value: totOreGps.toFixed(1) },
        { label: 'Ore Cliente', value: totOreCliente.toFixed(1) },
        { label: 'Ore Extra', value: totOreExtra.toFixed(1) },
        { label: 'Km Totali', value: `${totKm}` },
        { label: 'N° Interventi', value: `${sorted.length}` },
    ];
    const kpiW = (W - 28) / kpis.length;
    kpis.forEach((k, i) => {
        const x = 14 + i * kpiW;
        doc.setFillColor(248, 250, 252);
        doc.setDrawColor(226, 232, 240);
        doc.roundedRect(x, kpiY, kpiW - 2, 16, 2, 2, 'FD');
        doc.setFontSize(7);
        doc.setTextColor(100, 116, 139);
        doc.setFont('helvetica', 'normal');
        doc.text(k.label.toUpperCase(), x + (kpiW - 2) / 2, kpiY + 5, { align: 'center' });
        doc.setFontSize(12);
        doc.setTextColor(30, 46, 69);
        doc.setFont('helvetica', 'bold');
        doc.text(k.value, x + (kpiW - 2) / 2, kpiY + 12, { align: 'center' });
    });

    // Tabella dettaglio
    const bodyRows = sorted.map(r => {
        const data = r.dataRelazione
            ? new Date(r.dataRelazione).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
            : '-';
        // Nostro riferimento, altrimenti Nr. Mandato (non quelli automatici "temp<n>"),
        // altrimenti l'id; per i turni il nome del servizio.
        const riferimento = typeof r.praticaRiferimento === 'string' ? r.praticaRiferimento.trim() : '';
        const nrMandato = r.praticaNome ? estraiNumeroMandato(r.praticaNome) : '-';
        const nrValido = nrMandato !== '-' && !/^(temp\d*|na|n\/a)$/i.test(nrMandato);
        const pratica = r.turnoId
            ? (r.praticaNome || '-')
            : riferimento || (nrValido ? nrMandato : (r.praticaId ? `Mandato #${r.praticaId}` : 'Da abbinare'));
        const soggetto = r.soggettoNome || '-';
        const orario = (r.orarioInizioIndagine && r.orarioFineIndagine)
            ? `${r.orarioInizioIndagine} – ${r.orarioFineIndagine}`
            : '-';
        const oreGps   = (r.oreGps   || 0) > 0 ? (r.oreGps).toFixed(1)   : '-';
        const oreExtra = (r.oreExtra || 0) > 0 ? (r.oreExtra).toFixed(1) : '-';
        const oreCliente = calcolaOreCliente(r.orarioInizioIndagine, r.orarioFineIndagine);
        const oreClienteStr = oreCliente > 0 ? oreCliente.toFixed(1) : '-';
        const km = (r.kmPercorsi || r.km || 0) > 0 ? `${r.kmPercorsi || r.km}` : '-';
        const autoAz = r.autoAziendale ? 'Aziendale' : 'Propria';

        return [data, pratica, soggetto, orario, oreGps, oreClienteStr, oreExtra, km, autoAz];
    });

    bodyRows.push([
        { content: 'TOTALI', colSpan: 4, styles: { fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
        { content: totOreGps > 0 ? totOreGps.toFixed(1) : '0',        styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
        { content: totOreCliente > 0 ? totOreCliente.toFixed(1) : '0', styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
        { content: totOreExtra > 0 ? totOreExtra.toFixed(1) : '0',     styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
        { content: totKm > 0 ? `${totKm}` : '0',                       styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
        { content: '',                                                   styles: { fillColor: [241, 245, 249] } },
    ] as any);

    autoTable(doc, {
        startY: kpiY + 22,
        head: [['Data', 'Pratica', 'Soggetto Investigato', 'Orario', 'Ore GPS', 'Ore Cliente', 'Ore Extra', 'Km', 'Veicolo']],
        body: bodyRows as any,
        theme: 'grid',
        styles: { fontSize: 7.5, cellPadding: 2.5, overflow: 'linebreak' },
        headStyles: { fillColor: [139, 26, 26], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
        columnStyles: {
            0: { cellWidth: 22, halign: 'center' },
            1: { cellWidth: 50 },
            2: { cellWidth: 52 },
            3: { cellWidth: 28, halign: 'center' },
            4: { cellWidth: 18, halign: 'center' },
            5: { cellWidth: 20, halign: 'center' },
            6: { cellWidth: 18, halign: 'center' },
            7: { cellWidth: 14, halign: 'center' },
            8: { cellWidth: 22, halign: 'center' },
        },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        didParseCell: (data) => {
            // Evidenzia auto aziendale
            if (data.column.index === 8 && data.cell.raw === 'Aziendale') {
                data.cell.styles.textColor = [37, 99, 235];
                data.cell.styles.fontStyle = 'bold';
            }
        },
        margin: { left: 14, right: 14 },
    });

    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(150);
        doc.text(`Pag. ${i} / ${pageCount}`, W - 14, doc.internal.pageSize.getHeight() - 6, { align: 'right' });
        doc.text('Documento ad uso interno — non riporta compensi né tariffe', 14, doc.internal.pageSize.getHeight() - 6);
    }

    doc.save(`Riepilogo_${nomeCollaboratore.replace(/\s+/g, '_')}_${meseLabel.replace(' ', '_')}.pdf`);
}

// ─── PDF senza costi da ReportPersonaleItem (ReportMensileView) ───
function buildDocSenzaCosti(stat: any, anno: number, mese: number): jsPDF {
    const meseLabel = `${MESI_IT[mese - 1]} ${anno}`;
    const attivita: any[] = stat.attivitaGiornaliere || [];

    const totOreInv = stat.oreInvestigazioni || 0;
    const totOreSic = stat.oreSicurezza || 0;
    const totKm     = stat.kmPercorsi || 0;

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();

    // Header navy
    doc.setFillColor(30, 46, 69);
    doc.rect(0, 0, W, 26, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('RIEPILOGO ATTIVITÀ', 14, 10);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(stat.nomeCompleto || '', 14, 17);
    doc.setFontSize(8);
    doc.text(meseLabel.toUpperCase(), 14, 23);
    doc.text(`Generato il ${new Date().toLocaleDateString('it-IT')}`, W - 14, 23, { align: 'right' });

    // KPI bar
    const kpiY = 32;
    const kpis = [
        { label: 'Ore Investigazioni', value: totOreInv.toFixed(1) },
        { label: 'Ore Sicurezza',      value: totOreSic.toFixed(1) },
        { label: 'Km Percorsi',        value: `${totKm}` },
        { label: 'N° Interventi',      value: `${attivita.length}` },
    ];
    const kpiW = (W - 28) / kpis.length;
    kpis.forEach((k, i) => {
        const x = 14 + i * kpiW;
        doc.setFillColor(248, 250, 252);
        doc.setDrawColor(226, 232, 240);
        doc.roundedRect(x, kpiY, kpiW - 2, 16, 2, 2, 'FD');
        doc.setFontSize(7);
        doc.setTextColor(100, 116, 139);
        doc.setFont('helvetica', 'normal');
        doc.text(k.label.toUpperCase(), x + (kpiW - 2) / 2, kpiY + 5, { align: 'center' });
        doc.setFontSize(12);
        doc.setTextColor(30, 46, 69);
        doc.setFont('helvetica', 'bold');
        doc.text(k.value, x + (kpiW - 2) / 2, kpiY + 12, { align: 'center' });
    });

    // Tabella
    const bodyRows = attivita.map((g: any) => [
        g.data ? new Date(g.data).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-',
        g.praticaNome || '-',
        g.orariCliente || '-',
        g.oreInvestigazioni > 0 ? g.oreInvestigazioni.toFixed(1) : '-',
        g.oreSicurezza > 0 ? g.oreSicurezza.toFixed(1) : '-',
        g.kmPercorsi > 0 ? `${g.kmPercorsi}` : '-',
    ]);

    bodyRows.push([
        { content: 'TOTALI', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
        { content: totOreInv > 0 ? totOreInv.toFixed(1) : '0', styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
        { content: totOreSic > 0 ? totOreSic.toFixed(1) : '0', styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
        { content: totKm > 0 ? `${totKm}` : '0',               styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
    ] as any);

    autoTable(doc, {
        startY: kpiY + 22,
        head: [['Data', 'Pratica', 'Orario Cliente', 'Ore Inv.', 'Ore Sic.', 'Km']],
        body: bodyRows as any,
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [139, 26, 26], textColor: 255, fontStyle: 'bold', fontSize: 8 },
        columnStyles: {
            0: { cellWidth: 26, halign: 'center' },
            1: { cellWidth: 'auto' },
            2: { cellWidth: 34, halign: 'center' },
            3: { cellWidth: 20, halign: 'center' },
            4: { cellWidth: 20, halign: 'center' },
            5: { cellWidth: 18, halign: 'center' },
        },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        margin: { left: 14, right: 14 },
    });

    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(150);
        doc.text(`Pag. ${i} / ${pageCount}`, W - 14, doc.internal.pageSize.getHeight() - 6, { align: 'right' });
        doc.text('Documento ad uso interno — non riporta compensi né tariffe', 14, doc.internal.pageSize.getHeight() - 6);
    }

    return doc;
}

export function generaAttivitaSenzaCostiSingolo(stat: any, anno: number, mese: number): void {
    const meseLabel = `${MESI_IT[mese - 1]} ${anno}`;
    const doc = buildDocSenzaCosti(stat, anno, mese);
    doc.save(`Attivita_${(stat.nomeCompleto || 'Collaboratore').replace(/\s+/g, '_')}_${meseLabel.replace(' ', '_')}.pdf`);
}

export function generaAttivitaSenzaCostiBlob(stat: any, anno: number, mese: number): Blob {
    return buildDocSenzaCosti(stat, anno, mese).output('blob');
}

// Un unico PDF con tutti i collaboratori (una sezione per ciascuno), senza costi
export function generaAttivitaSenzaCostiCompleto(stats: any[], anno: number, mese: number): void {
    const meseLabel = `${MESI_IT[mese - 1]} ${anno}`;
    if (!stats.length) return;

    const firstDoc = buildDocSenzaCosti(stats[0], anno, mese);

    // jsPDF non supporta il merge: si costruisce un unico documento iterando tutti gli stat.
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();

    stats.forEach((stat, idx) => {
        if (idx > 0) doc.addPage();

        // Header navy
        doc.setFillColor(30, 46, 69);
        doc.rect(0, 0, W, 26, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.text('RIEPILOGO ATTIVITÀ', 14, 10);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(stat.nomeCompleto || '', 14, 17);
        doc.setFontSize(8);
        doc.text(meseLabel.toUpperCase(), 14, 23);
        doc.text(`Generato il ${new Date().toLocaleDateString('it-IT')}`, W - 14, 23, { align: 'right' });

        const attivita: any[] = stat.attivitaGiornaliere || [];
        const totOreInv = stat.oreInvestigazioni || 0;
        const totOreSic = stat.oreSicurezza || 0;
        const totKm     = stat.kmPercorsi || 0;

        // KPI bar
        const kpiY = 32;
        const kpis = [
            { label: 'Ore Investigazioni', value: totOreInv.toFixed(1) },
            { label: 'Ore Sicurezza',      value: totOreSic.toFixed(1) },
            { label: 'Km Percorsi',        value: `${totKm}` },
            { label: 'N° Interventi',      value: `${attivita.length}` },
        ];
        const kpiW = (W - 28) / kpis.length;
        kpis.forEach((k, i) => {
            const x = 14 + i * kpiW;
            doc.setFillColor(248, 250, 252);
            doc.setDrawColor(226, 232, 240);
            doc.roundedRect(x, kpiY, kpiW - 2, 16, 2, 2, 'FD');
            doc.setFontSize(7);
            doc.setTextColor(100, 116, 139);
            doc.setFont('helvetica', 'normal');
            doc.text(k.label.toUpperCase(), x + (kpiW - 2) / 2, kpiY + 5, { align: 'center' });
            doc.setFontSize(12);
            doc.setTextColor(30, 46, 69);
            doc.setFont('helvetica', 'bold');
            doc.text(k.value, x + (kpiW - 2) / 2, kpiY + 12, { align: 'center' });
        });

        const bodyRows = attivita.map((g: any) => [
            g.data ? new Date(g.data).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-',
            g.praticaNome || '-',
            g.orariCliente || '-',
            g.oreInvestigazioni > 0 ? g.oreInvestigazioni.toFixed(1) : '-',
            g.oreSicurezza > 0 ? g.oreSicurezza.toFixed(1) : '-',
            g.kmPercorsi > 0 ? `${g.kmPercorsi}` : '-',
        ]);
        bodyRows.push([
            { content: 'TOTALI', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
            { content: totOreInv > 0 ? totOreInv.toFixed(1) : '0', styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
            { content: totOreSic > 0 ? totOreSic.toFixed(1) : '0', styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
            { content: totKm > 0 ? `${totKm}` : '0',               styles: { fontStyle: 'bold', halign: 'center', fillColor: [241, 245, 249], textColor: [30, 46, 69] } },
        ] as any);

        autoTable(doc, {
            startY: kpiY + 22,
            head: [['Data', 'Pratica', 'Orario Cliente', 'Ore Inv.', 'Ore Sic.', 'Km']],
            body: bodyRows as any,
            theme: 'grid',
            styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
            headStyles: { fillColor: [139, 26, 26], textColor: 255, fontStyle: 'bold', fontSize: 8 },
            columnStyles: {
                0: { cellWidth: 26, halign: 'center' },
                1: { cellWidth: 'auto' },
                2: { cellWidth: 34, halign: 'center' },
                3: { cellWidth: 20, halign: 'center' },
                4: { cellWidth: 20, halign: 'center' },
                5: { cellWidth: 18, halign: 'center' },
            },
            alternateRowStyles: { fillColor: [249, 250, 251] },
            margin: { left: 14, right: 14 },
        });
    });

    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(150);
        doc.text(`Pag. ${i} / ${pageCount}`, W - 14, H - 6, { align: 'right' });
        doc.text('Documento ad uso interno — non riporta compensi né tariffe', 14, H - 6);
    }

    doc.save(`Attivita_Completo_${meseLabel.replace(' ', '_')}.pdf`);

    void firstDoc; // serve solo per l'inferenza del tipo
}
